/** Matches work item keys such as `KBX-142` in free text. */
export const ITEM_KEY_PATTERN = /\b([A-Z][A-Z0-9]{1,9})-(\d+)\b/g;
export const ITEM_KEY_EXACT = /^([A-Z][A-Z0-9]{1,9})-(\d+)$/;
export const PROJECT_KEY_PATTERN = /^[A-Z][A-Z0-9]{1,9}$/;

/**
 * Extracts work item keys from free text. When `allowedProjectKeys` is given, keys of other projects
 * are ignored (CI parsing uses the project's key allow-list, not a hard-coded `KBX-` pattern; §7.7).
 */
export function extractItemKeys(text: string, allowedProjectKeys?: readonly string[]): string[] {
  const allow = allowedProjectKeys ? new Set(allowedProjectKeys.map((k) => k.toUpperCase())) : null;
  const keys = new Set<string>();
  for (const m of text.matchAll(ITEM_KEY_PATTERN)) {
    if (!allow || allow.has(m[1])) keys.add(m[0]);
  }
  return [...keys];
}

/** True when a commit message is a revert (`Revert "…"` / `This reverts commit …`). */
export function isRevertMessage(message: string): boolean {
  return /^revert\b/i.test(message.trim()) || /this reverts commit [0-9a-f]{7,40}/i.test(message);
}

/**
 * Parses a human duration (`2h 30m`, `1d`, `45m`, `1.5h`, `90`) into minutes (FR-TIME-2).
 * A bare number is treated as minutes. `d` uses the configured working hours per day.
 * Returns null when the input is not a valid positive duration.
 */
export function parseDuration(input: string, hoursPerDay = 8): number | null {
  const s = input.trim().toLowerCase();
  if (!s) return null;
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return n > 0 ? n : null;
  }
  const re = /(\d+(?:\.\d+)?)\s*(w|d|h|m)/g;
  let total = 0;
  let consumed = '';
  for (const m of s.matchAll(re)) {
    const n = Number(m[1]);
    const unit = m[2];
    total += unit === 'w' ? n * 5 * hoursPerDay * 60 : unit === 'd' ? n * hoursPerDay * 60 : unit === 'h' ? n * 60 : n;
    consumed += m[0];
  }
  if (consumed.replace(/\s/g, '') !== s.replace(/\s/g, '')) return null;
  const minutes = Math.round(total);
  return minutes > 0 ? minutes : null;
}

/** Formats minutes as `2h 30m` (days are not used so that totals stay comparable to an 8h day). */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes == null) return '—';
  const sign = minutes < 0 ? '-' : '';
  const m = Math.abs(Math.round(minutes));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${sign}${h}h ${r}m`;
  if (h) return `${sign}${h}h`;
  return `${sign}${r}m`;
}

// ---------------------------------------------------------------------------------------------
// Rich text (TipTap / ProseMirror JSON) helpers
// ---------------------------------------------------------------------------------------------

export interface RichNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: RichNode[];
  text?: string;
  marks?: { type: string; attrs?: Record<string, unknown> }[];
}

const BLOCK_TYPES = new Set([
  'paragraph',
  'heading',
  'listItem',
  'taskItem',
  'codeBlock',
  'blockquote',
  'tableRow',
  'horizontalRule',
]);

/** Plain-text projection of a rich document, used for search indexing and AI context. */
export function richTextToPlain(doc: unknown): string {
  if (doc == null) return '';
  if (typeof doc === 'string') return doc;
  const out: string[] = [];
  const walk = (n: RichNode) => {
    if (n.type === 'text' && n.text) out.push(n.text);
    else if (n.type === 'mention') out.push(`@${String(n.attrs?.label ?? n.attrs?.id ?? '')}`);
    else if (n.type === 'workItemChip') out.push(String(n.attrs?.itemKey ?? ''));
    else if (n.type === 'hardBreak') out.push('\n');
    n.content?.forEach(walk);
    if (BLOCK_TYPES.has(n.type)) out.push('\n');
  };
  walk(doc as RichNode);
  return out
    .join('')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Builds a minimal rich document from plain text (one paragraph per line). */
export function plainToRichText(text: string): RichNode {
  const lines = text.split(/\r?\n/);
  return {
    type: 'doc',
    content: lines.map((line) => ({
      type: 'paragraph',
      content: line ? [{ type: 'text', text: line }] : undefined,
    })),
  };
}

/** Returns ids of users mentioned in a rich document (`mention` nodes with `attrs.id`). */
export function extractMentions(doc: unknown): { userIds: string[]; groupHandles: string[] } {
  const userIds = new Set<string>();
  const groupHandles = new Set<string>();
  const walk = (n: RichNode) => {
    if (n.type === 'mention' && n.attrs) {
      if (n.attrs.kind === 'group' && typeof n.attrs.id === 'string') groupHandles.add(n.attrs.id);
      else if (typeof n.attrs.id === 'string') userIds.add(n.attrs.id);
    }
    n.content?.forEach(walk);
  };
  if (doc && typeof doc === 'object') walk(doc as RichNode);
  return { userIds: [...userIds], groupHandles: [...groupHandles] };
}

/** Finds a block (any node with attrs.blockId) by id and returns it with its plain text. */
export function findBlock(doc: unknown, blockId: string): RichNode | null {
  let found: RichNode | null = null;
  const walk = (n: RichNode) => {
    if (found) return;
    if (n.attrs?.blockId === blockId) {
      found = n;
      return;
    }
    n.content?.forEach(walk);
  };
  if (doc && typeof doc === 'object') walk(doc as RichNode);
  return found;
}

/** Immutably maps nodes of a rich document. */
export function mapRichNodes(doc: RichNode, fn: (n: RichNode) => RichNode): RichNode {
  const next = fn(doc);
  if (!next.content) return next;
  return { ...next, content: next.content.map((c) => mapRichNodes(c, fn)) };
}

export function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

/** Formats a Date as YYYY-MM-DD in the given IANA timezone (default IST). */
export function toLocalDate(d: Date, timeZone = 'Asia/Kolkata'): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** ISO weekday 1 (Mon) … 7 (Sun) for a YYYY-MM-DD date. */
export function isoWeekday(date: string): number {
  const d = new Date(`${date}T00:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
}

/** Monday (ISO week start) of the week containing a YYYY-MM-DD date. */
export function weekStart(date: string): string {
  return addDays(date, 1 - isoWeekday(date));
}

/**
 * Minimal Markdown → TipTap JSON for AI-drafted guides (headings, bullet/numbered lists, paragraphs,
 * **bold** and `code`). Anything else becomes plain paragraph text; output is still sanitised server-side.
 */
export interface MdNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: MdNode[];
  text?: string;
  marks?: { type: string }[];
}
export function markdownToRichText(md: string): MdNode {
  type N = MdNode;
  const inline = (line: string): N[] => {
    const out: N[] = [];
    const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
    let last = 0;
    for (const m of line.matchAll(re)) {
      if (m.index! > last) out.push({ type: 'text', text: line.slice(last, m.index) });
      const t = m[0];
      out.push(t.startsWith('**') ? { type: 'text', text: t.slice(2, -2), marks: [{ type: 'bold' }] } : { type: 'text', text: t.slice(1, -1), marks: [{ type: 'code' }] });
      last = m.index! + t.length;
    }
    if (last < line.length) out.push({ type: 'text', text: line.slice(last) });
    return out.filter((n) => n.text);
  };
  const content: N[] = [];
  let list: N | null = null;
  for (const raw of md.replace(/\r/g, '').split('\n')) {
    const line = raw.trimEnd();
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const num = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (bullet || num) {
      const type = bullet ? 'bulletList' : 'orderedList';
      if (!list || list.type !== type) {
        list = { type, content: [] };
        content.push(list);
      }
      list.content!.push({ type: 'listItem', content: [{ type: 'paragraph', content: inline((bullet ?? num)![1]) }] });
      continue;
    }
    list = null;
    if (h) content.push({ type: 'heading', attrs: { level: Math.min(4, h[1].length) }, content: inline(h[2]) });
    else if (line.trim()) content.push({ type: 'paragraph', content: inline(line.trim()) });
  }
  return { type: 'doc', content: content.length ? content : [{ type: 'paragraph' }] };
}
